import { paginationService } from "@/src/shared/services/pagination";
import type {
  IPublicReview,
  IPublicReviewPage,
  IReview,
  IReviewPage,
} from "./types";

export const reviewService = {
  /** Next page number, or undefined when the last page is loaded. */
  nextPage(lastPage: IReviewPage | undefined): number | undefined {
    return paginationService.nextPage(lastPage);
  },

  /** Python pages: the next page, or undefined after the last (TanStack stops there). */
  nextPublicPage(lastPage: IPublicReviewPage | undefined): number | undefined {
    const pagination = lastPage?.pagination;
    return pagination?.total_pages != null &&
      pagination.page < pagination.total_pages
      ? pagination.page + 1
      : undefined;
  },

  /** "Rina Akter", or "Guest" when the author left no name. */
  authorName(author: IPublicReview["author"]): string {
    const name = [author?.first_name, author?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
    return name || "Guest";
  },

  /** The app's caption under a reviewer: "2 years on Travela" / "9 months on Travela". */
  authorTenure(months: number | null | undefined): string | null {
    if (months == null) return null;
    if (months >= 12) {
      const years = Math.floor(months / 12);
      return `${years} ${years === 1 ? "year" : "years"} on Travela`;
    }
    const shown = Math.max(1, months);
    return `${shown} ${shown === 1 ? "month" : "months"} on Travela`;
  },

  /** "July 2026": when the review was written, as Airbnb's review cards show it. */
  reviewMonth(createdAt: string | null | undefined): string | null {
    if (!createdAt) return null;
    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  },

  /**
   * The review body cut into plain and highlighted runs, as the app's
   * `highlightedReviewSpans`. Offsets count characters (code points, as Python does),
   * so an emoji before a match does not shift it. Out-of-range spans are clamped.
   */
  highlightSegments(
    body: string,
    highlights: { start: number; end: number }[] | undefined,
  ): { text: string; highlighted: boolean }[] {
    const chars = Array.from(body);
    if (!highlights?.length || chars.length === 0) {
      return body ? [{ text: body, highlighted: false }] : [];
    }
    const clamp = (n: number) => Math.min(Math.max(n, 0), chars.length);
    const segments: { text: string; highlighted: boolean }[] = [];
    let plainStart = 0;
    for (const { start: rawStart, end: rawEnd } of [...highlights].sort(
      (a, b) => a.start - b.start,
    )) {
      const start = Math.max(clamp(rawStart), plainStart);
      const end = clamp(rawEnd);
      if (start > plainStart) {
        segments.push({
          text: chars.slice(plainStart, start).join(""),
          highlighted: false,
        });
      }
      if (end > start) {
        segments.push({
          text: chars.slice(start, end).join(""),
          highlighted: true,
        });
        plainStart = end;
      }
    }
    if (plainStart < chars.length) {
      segments.push({
        text: chars.slice(plainStart).join(""),
        highlighted: false,
      });
    }
    return segments;
  },

  /** All loaded reviews in order, skipping malformed pages and empty rows. */
  flatten(pages: (IReviewPage | undefined)[] | undefined): IReview[] {
    return (pages ?? []).flatMap((page) =>
      Array.isArray(page?.data) ? page.data.filter(Boolean) : [],
    );
  },
};
